import { Lock } from 'lucide-react';

import type { Persona, ResearchRoomMessage } from '@repo/contracts';

import { YesNoBar } from '../persona-lookalikes/lookalikes-poll';

function Header({ subtitle }: { subtitle: string }) {
  return (
    <header className="flex items-center gap-2">
      <Lock aria-hidden="true" className="size-4 text-text-muted" />
      <p className="flex-1 truncate text-ui">
        <span className="font-semibold text-text-primary">Lookalike poll</span>
        <span className="text-text-muted"> · {subtitle}</span>
      </p>
      <span className="shrink-0 text-tag text-text-muted">
        Only you see this
      </span>
    </header>
  );
}

/**
 * A lookalike poll, as it sits in the transcript — private to the researcher,
 * never something a persona hears (see `heardLines`). Tight and brief like
 * `PollCard`: just the yes/no split, not the full per-cluster/per-lookalike
 * breakdown.
 */
export function LookalikePollCard({
  message,
  personas,
}: {
  message: ResearchRoomMessage;
  personas: Map<string, Persona>;
}) {
  const poll = message.lookalikePoll;
  if (!poll) return null;
  const persona = personas.get(poll.personaId);

  return (
    <article className="rounded-card bg-surface px-4 py-3.5 ring-1 ring-hairline duration-200 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none">
      <Header
        subtitle={`${poll.total.toLocaleString()} of ${persona?.name ?? 'a persona'}`}
      />
      <p className="mt-2 text-ui text-text-secondary" title={poll.question}>
        &ldquo;{poll.question}&rdquo;
      </p>
      <div className="mt-3">
        <YesNoBar no={poll.no} unknown={poll.unknown} yes={poll.yes} />
      </div>
    </article>
  );
}

/**
 * Shown the instant the researcher asks — before the population is even
 * sampled, let alone judged — so the room never looks like the question
 * went nowhere while Jev works through it.
 */
export function LookalikePollPending({
  personaName,
  question,
}: {
  personaName: string;
  question: string;
}) {
  return (
    <article className="rounded-card bg-surface px-4 py-3.5 opacity-70 ring-1 ring-hairline duration-200 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none">
      <Header subtitle={personaName} />
      <p className="mt-2 text-ui text-text-secondary" title={question}>
        &ldquo;{question}&rdquo;
      </p>
      <p className="mt-3 animate-pulse text-ui text-text-muted motion-reduce:animate-none">
        Asking the lookalikes…
      </p>
    </article>
  );
}
